"use client"

import { useEffect, useRef, useState } from "react"
import { Task } from "@/schema"
import { toValidDate, toZonedDate } from "@/utils/format-date"
import { Button, Calendar as CalendarUI, DatePopover, PopoverContent, PopoverTrigger } from "@ui/shad"
import { differenceInCalendarDays, format, isSameDay } from "date-fns"
import { CalendarDays, Clock, X } from "lucide-react"

import { useBookTimezone } from "@/hooks/use-book-timezone"
import { TimeSelect } from "@/components/_ui/time-picker/time-select"
import { dateAtStartOfDayInTimeZone } from "@/components/tasks/utils/deadline-payload"

interface DeadlineProps {
  task: Task
  onDateUpdate: (date?: Date | null, allDay?: boolean) => void
  isCompleted?: boolean
}

export function Deadline({ task, onDateUpdate, isCompleted = false }: DeadlineProps) {
  const [showDateSelector, setShowDateSelector] = useState(false)
  const [showTime, setShowTime] = useState(task.all_day === false)
  const [localTimeValue, setLocalTimeValue] = useState<string | null>(null)
  const bookTimezone = useBookTimezone(task.book_id)
  // All-day deadlines anchor to the book's timezone (see Task#adjust_deadline_for_all_day);
  // timed deadlines stay in the viewer's browser timezone.
  const effectiveTimeZone = task.all_day !== false ? bookTimezone : undefined

  useEffect(() => {
    setShowTime(task.all_day === false)
  }, [task.all_day])

  const prevDeadlineRef = useRef(task.deadline_at)
  const prevAllDayRef = useRef(task.all_day)

  if (task.deadline_at !== prevDeadlineRef.current || task.all_day !== prevAllDayRef.current) {
    prevDeadlineRef.current = task.deadline_at
    prevAllDayRef.current = task.all_day
    if (localTimeValue !== null) setLocalTimeValue(null)
  }

  // `deadline_at` arrives from the API as an ISO string (despite the Date type),
  // so normalize to a real Date before any Date math, formatting, or picker use.
  const deadline = toValidDate(task.deadline_at)

  const formatDeadline = (date?: Date | string | null) => {
    const raw = toValidDate(date)
    if (!raw) return null

    const d = toZonedDate(raw, effectiveTimeZone)
    const now = toZonedDate(new Date(), effectiveTimeZone)
    const timeSuffix = !task.all_day ? ` @ ${format(raw, "h:mm")}` : ""

    if (isSameDay(d, now)) return `Today${timeSuffix}`
    if (isSameDay(d, new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1))) {
      return `Tomorrow${timeSuffix}`
    }
    if (isSameDay(d, new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1))) {
      return `Yesterday${timeSuffix}`
    }

    const daysDifference = differenceInCalendarDays(d, now)
    if (daysDifference > 0 && daysDifference <= 6) {
      return `In ${daysDifference} ${daysDifference === 1 ? "day" : "days"}${timeSuffix}`
    } else if (daysDifference < 0 && daysDifference >= -6) {
      const daysAgo = Math.abs(daysDifference)
      return `${daysAgo} ${daysAgo === 1 ? "day" : "days"} ago${timeSuffix}`
    }
    return format(d, !task.all_day ? "MMM d '@' h:mm" : "MMM d")
  }

  const handleTimeChange = (timeString: string) => {
    if (!deadline) return
    const d = deadline
    if (timeString === "none") {
      const newDate = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0)
      setLocalTimeValue("none")
      onDateUpdate(dateAtStartOfDayInTimeZone(newDate, bookTimezone), true)
      setShowTime(false)
      return
    }
    const [hours, minutes] = timeString.split(":")
    const newDate = new Date(d.getFullYear(), d.getMonth(), d.getDate(), parseInt(hours), parseInt(minutes), 0, 0)
    setLocalTimeValue(timeString)
    onDateUpdate(newDate, false)
    if (!showTime) setShowTime(true)
  }

  const getTimeValue = (): string => {
    if (localTimeValue !== null) return localTimeValue
    if (!showTime || !deadline) return "none"
    const d = deadline
    const h = d.getHours()
    const m = d.getMinutes()
    if (h === 0 && m === 0) return "none"
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
  }

  if (!task.deadline_at) return null

  return (
    <div className="date-selector" onClick={(e) => e.stopPropagation()}>
      <DatePopover open={showDateSelector} onOpenChange={(open) => !isCompleted && setShowDateSelector(open)}>
        <PopoverTrigger asChild disabled={isCompleted}>
          <button className="text-muted-foreground hover:border-foreground hover:text-foreground flex shrink-0 items-center gap-1 border-b border-dashed border-transparent pb-px text-sm/none whitespace-nowrap">
            <CalendarDays className="mb-px size-3" />
            <div>{formatDeadline(task.deadline_at) || "No due date"}</div>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <CalendarUI
            mode="single"
            selected={deadline ?? undefined}
            onSelect={(date) => {
              if (!date) {
                onDateUpdate(null, true)
                setShowDateSelector(false)
                return
              }
              if (deadline && showTime) {
                date.setHours(deadline.getHours(), deadline.getMinutes())
              }
              onDateUpdate(showTime ? date : dateAtStartOfDayInTimeZone(date, bookTimezone), !showTime)
              setShowDateSelector(false)
            }}
            initialFocus
          />
          <div className="border-border border-t px-3 py-2">
            <div className="flex h-7 items-center gap-2">
              <Clock className="h-4 w-4 opacity-70" />
              <TimeSelect value={getTimeValue()} onChange={handleTimeChange} size="xs" includeNone />
              {task.deadline_at && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-auto"
                  onClick={() => {
                    onDateUpdate(null, true)
                    setShowTime(false)
                    setShowDateSelector(false)
                  }}
                >
                  <X />
                  Clear date
                </Button>
              )}
            </div>
          </div>
        </PopoverContent>
      </DatePopover>
    </div>
  )
}
